'use client';

import { useTranslations } from 'next-intl';
import { CaretUp } from 'assets/icons';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import useScrollspy from 'hooks/scroll/useScrollSpy';
import { isReviewsSectionEnabled } from 'utils/featureFlags';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Container } from '..';
import styles from './SecondaryNavbar.module.css';

const ALL_SECTIONS = [
  { id: 'ring-contents', messageId: 'ring.secondaryHeader.a' },
  { id: 'ring-reviews', messageId: 'ring.secondaryHeader.g' },
  { id: 'ring-sleep', messageId: 'ring.secondaryHeader.b' },
  { id: 'ring-comparision', messageId: 'ring.secondaryHeader.c' },
  { id: 'ring-capabilities', messageId: 'ring.secondaryHeader.d' },
  { id: 'ring-specs', messageId: 'ring.secondaryHeader.e' },
  { id: 'ring-faq', messageId: 'ring.secondaryHeader.f' },
] as const;

type SectionConfig = (typeof ALL_SECTIONS)[number];
type SectionId = SectionConfig['id'];

export const SecondaryNavbar_RingAir = ({
  className,
}: {
  className?: string;
}) => {
    const GlobalUI = useGlobalUI();
    const t = useTranslations('ring');
    const { region, language } = useRegionLocale();

    const sectionConfig = useMemo<readonly SectionConfig[]>(
      () =>
        isReviewsSectionEnabled({ region, language })
          ? ALL_SECTIONS
          : ALL_SECTIONS.filter((section) => section.id !== 'ring-reviews'),
      [region, language],
    );
    const sectionIds = useMemo<SectionId[]>(
      () => sectionConfig.map((section) => section.id),
      [sectionConfig],
    );

    const [isMobileNavVisible, setIsMobileNavVisible] = useState(false);
    const isScrollingRef = useRef(false);
    const scrollUpdateCallbackRef = useRef<(() => void) | null>(null);

    const setActiveSection = useCallback(
      (sectionId: SectionId) => {
        if (typeof window === 'undefined') return;

        const element = document.getElementById(sectionId);
        if (!element) return;

        // Disable scrollspy during programmatic scrolling
        isScrollingRef.current = true;

        // Use the same offset as scrollspy (60px)
        const scrollspyOffset = 30;

        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.scrollY - scrollspyOffset;

        // Use a more reliable scroll method
        const startPosition = window.scrollY;
        const distance = offsetPosition - startPosition;
        const duration = 800;
        let start: number | null = null;

        const animation = (currentTime: number) => {
          if (start === null) start = currentTime;
          const timeElapsed = currentTime - start;
          const progress = Math.min(timeElapsed / duration, 1);

          // Easing function for smooth animation
          const ease =
            progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

          window.scrollTo(0, startPosition + distance * ease);

          if (timeElapsed < duration) {
            requestAnimationFrame(animation);
          } else {
            // Re-enable scrollspy and trigger update
            setTimeout(() => {
              isScrollingRef.current = false;
              // Manually trigger scrollspy update
              if (scrollUpdateCallbackRef.current) {
                scrollUpdateCallbackRef.current();
              }
            }, 100);
          }
        };

        requestAnimationFrame(animation);
      },
      [GlobalUI.headerVisible, GlobalUI.headerHeight],
    );

    const activeId = useScrollspy(
      sectionIds,
      60,
      isScrollingRef,
      scrollUpdateCallbackRef,
    );
    const activeSection =
      sectionConfig.find((section) => section.id === activeId) ??
      sectionConfig[0];

    const SecondaryNav = (
      <div className={`${styles.nav} u-hide-scrollbar`}>
        {sectionConfig.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`${styles.navItem}${
              activeId === section.id ? ` ${styles.active}` : ''
            }`}
            data-buttontype={`secondary_navbar_${section.id}`}
          >
            {t(section.messageId)}
          </button>
        ))}
      </div>
    );

    const SecondaryNavMobile = (
      <div className={styles.mobileNavColumn}>
        {sectionConfig.map((section, index) => (
          <button
            key={section.id}
            onClick={() => {
              setActiveSection(section.id);
              setIsMobileNavVisible(false);
            }}
            className={`${styles.mobileNavItem}${
              activeId === section.id ? ` ${styles.active}` : ''
            }`}
            style={{ animationDelay: `${index * 0.05}s` }}
            data-buttontype={`secondary_navbar_${section.id}_mobile`}
          >
            {t(section.messageId)}
          </button>
        ))}
      </div>
    );

    const stickyTop = GlobalUI.headerVisible ? GlobalUI.headerHeight ?? 0 : 0;

    return (
      <div
        className={`${styles.root}${className ? ` ${className}` : ''}`}
        style={
          {
            top: `${stickyTop}px`,
            '--subnav-top': `${stickyTop}px`,
          } as CSSProperties
        }
      >
        <Container onClick={() => setIsMobileNavVisible(false)} aria-hidden>
          <div className={`${styles.content} d-sm-none`}>{SecondaryNav}</div>
          <div className={`${styles.contentMobile} d-lg-none`} aria-hidden>
            <p
              className={styles.sectionTitleVisible}
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileNavVisible((prev) => !prev);
              }}
            >
              {t(activeSection.messageId)}{' '}
              <CaretUp
                fill="#000000"
                style={{ transform: 'rotate(180deg)', height: '8px' }}
              />
            </p>
            {isMobileNavVisible ? (
              <div
                className={`${styles.mobileNav} u-hide-scrollbar`}
                onClick={(e) => e.stopPropagation()}
              >
                {SecondaryNavMobile}
              </div>
            ) : null}
          </div>
        </Container>
      </div>
    );
};
